import React, { useEffect, useState } from "react";
import { EMPTY_RECORD, useDetailRecord } from 'helpers/detailRequest';
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { LoadingSpinner, Modal, Button, DetailRecordFallback } from 'components/common';
import {
  getPurchaseOrder, submitPurchaseOrder, withdrawPurchaseOrder, deletePurchaseOrder, clearPurchaseOrder,
  openPurchaseOrderPdf, downloadPurchaseOrderExcel, duplicatePurchaseOrder,
} from "redux/erp/purchaseOrderSlice";
import PageShell, { PageTitle, SectionHeading } from "../purchase/common/PageShell";
import Clickable from "../purchase/common/Clickable";
import PurchaseOrderDrawer from "./PurchaseOrderDrawer";
import ApproveForm from "./detail/ApproveForm";
import RejectForm from "./detail/RejectForm";
import CancelForm from "./detail/CancelForm";
import useBreadcrumb from "hooks/useBreadcrumb";

const STATUS = {
  0: { name: "Draft", cls: "tw-bg-geist-100 tw-text-geist-fg-2 tw-border-geist-border" },
  1: { name: "Pending", cls: "tw-bg-[#fff8ee] tw-text-[#b46a00] tw-border-[#f7e2c0]" },
  2: { name: "Approved", cls: "tw-bg-[#e9f9f6] tw-text-[#04766a] tw-border-[#bfeae4]" },
  3: { name: "Rejected", cls: "tw-bg-[#fdeceb] tw-text-[#c0362f] tw-border-[#f6cfcc]" },
  4: { name: "Canceled", cls: "tw-bg-geist-100 tw-text-geist-fg-3 tw-border-geist-border" },
};

const money = (v) => (Number(v) || 0).toLocaleString("en-US");

// Read-only field — label above value, no border. (Previously had a bottom border that made
// every value look like a disabled input box on a page that's not editable.)
const InfoRow = ({ label, value }) => (
  <div className="tw-py-1.5">
    <div className="tw-text-[11px] tw-text-geist-fg-3 tw-mb-0.5">{label}</div>
    <div className="tw-text-[13px] tw-text-geist-fg tw-font-medium">{value || "—"}</div>
  </div>
);

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const PurchaseOrderDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Only the record in the URL — the persisted store can still hold the one
  // viewed last (e.g. when opened from an email link).
  const { record, waiting, error } = useDetailRecord('erp_purchase_order', id);
  const po = record || EMPTY_RECORD;
  const [editOpen, setEditOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    dispatch(getPurchaseOrder(id));
    return () => dispatch(clearPurchaseOrder());
  }, [dispatch, id]);

  // Breadcrumb động: hiện mã phiếu thay vì id.
  useBreadcrumb(
    po && po.id
      ? [
          { label: "Purchase Orders", to: "/purchase-orders" },
          { label: po.code || `PO ${po.id}` },
        ]
      : null
  );

  if (!record && waiting) return <PageShell header={<div className="tw-p-4"><PageTitle>Purchase Order</PageTitle></div>}><LoadingSpinner message="Loading..." /></PageShell>;
  if (!record) return <DetailRecordFallback waiting={false} title="Purchase Order not available" error={error} onRetry={() => dispatch(getPurchaseOrder(id))} />;

  const st = STATUS[po.status?.id ?? po.status] || STATUS[0];
  const perm = po.permissions || {};

  const onSubmit = async () => {
    setBusy(true);
    try {
      await dispatch(submitPurchaseOrder(po.id)).unwrap();
      toast.success("Submitted for approval.");
    } catch (e) { toast.error(e?.error || e?.message || "Submit failed."); }
    finally { setBusy(false); }
  };

  const onWithdraw = async () => {
    setBusy(true);
    try {
      await dispatch(withdrawPurchaseOrder(po.id)).unwrap();
      toast.success("Withdrawn to draft.");
    } catch (e) { toast.error(e?.error || e?.message || "Withdraw failed."); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    setBusy(true);
    try {
      await dispatch(deletePurchaseOrder(po.id)).unwrap();
      toast.success("Purchase order deleted.");
      navigate("/purchase-orders");
    } catch (e) { toast.error(e?.error || e?.message || "Delete failed."); }
    finally { setBusy(false); setDeleteOpen(false); }
  };

  const onPrint = async () => {
    setPdfLoading(true);
    try { await openPurchaseOrderPdf(po.id); }
    catch (e) { toast.error(e?.response?.data?.error || "Failed to open PDF."); }
    finally { setPdfLoading(false); }
  };

  const onDownloadExcel = async () => {
    setExcelLoading(true);
    try { await downloadPurchaseOrderExcel(po.id); }
    catch (e) { toast.error(e?.response?.data?.error || "Failed to download Excel."); }
    finally { setExcelLoading(false); }
  };

  const onDuplicate = async () => {
    setDuplicating(true);
    try {
      const res = await dispatch(duplicatePurchaseOrder(po.id)).unwrap();
      const newPo = res?.data?.attributes || res?.data || {};
      toast.success(`Duplicated into new draft ${newPo.code || ""}.`);
      navigate(`/purchase-orders/${newPo.id}`);
    } catch (e) { toast.error(e?.error || e?.message || "Duplicate failed."); }
    finally { setDuplicating(false); }
  };

  const header = (
    <div className="tw-px-4 sm:tw-px-6 tw-py-4 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-3">
      <div className="tw-flex tw-items-center tw-gap-3">
        <Clickable onClick={() => navigate("/purchase-orders")} className="tw-text-geist-fg-3 hover:tw-text-geist-fg">
          <i className="bi bi-arrow-left tw-text-lg" />
        </Clickable>
        <PageTitle subtitle="QF-PUR-03 — Purchase Order">{po.code}</PageTitle>
        <span className={`tw-inline-flex tw-items-center tw-text-[11px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-xl tw-border tw-border-solid ${st.cls}`}>{st.name}</span>
      </div>
      <div className="tw-flex tw-items-center tw-gap-2">
        {perm.allow_edit && (
          <Clickable onClick={() => setEditOpen(true)} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
            <i className="bi bi-pencil" /> Edit
          </Clickable>
        )}
        {perm.allow_delete && (
          <Clickable onClick={() => setDeleteOpen(true)} disabled={busy} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-error tw-text-geist-error tw-text-[13px] tw-font-medium hover:tw-bg-[#fdeceb]">
            <i className="bi bi-trash" /> Delete
          </Clickable>
        )}
        {perm.allow_submit && (
          <Clickable onClick={onSubmit} disabled={busy} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-accent tw-text-white tw-text-[13px] tw-font-medium hover:tw-bg-geist-accent-2">
            <i className="bi bi-send" /> Submit
          </Clickable>
        )}
        {perm.allow_withdraw && (
          <Clickable onClick={onWithdraw} disabled={busy} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100">
            <i className="bi bi-arrow-counterclockwise" /> Withdraw
          </Clickable>
        )}
        {perm.allow_reject && (
          <Clickable onClick={() => setRejectOpen(true)} disabled={busy} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-error tw-text-geist-error tw-text-[13px] tw-font-medium hover:tw-bg-[#fdeceb]">
            <i className="bi bi-x-lg" /> Reject
          </Clickable>
        )}
        {perm.allow_cancel && (
          <Clickable onClick={() => setCancelOpen(true)} disabled={busy} title="Cancel this order permanently (different from Withdraw)" className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-error tw-text-geist-error tw-text-[13px] tw-font-medium hover:tw-bg-[#fdeceb]">
            <i className="bi bi-ban" /> Cancel
          </Clickable>
        )}
        {perm.allow_duplicate && (
          <Clickable onClick={onDuplicate} disabled={duplicating} title="Create a new draft with this order's content" className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100 disabled:tw-opacity-60">
            {duplicating ? <i className="bi bi-arrow-repeat tw-animate-spin" /> : <i className="bi bi-files" />} Duplicate
          </Clickable>
        )}
        {perm.allow_approve && (
          <Clickable onClick={() => setApproveOpen(true)} disabled={busy} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3.5 tw-rounded-geist-sm tw-bg-geist-success tw-text-white tw-text-[13px] tw-font-medium hover:tw-opacity-90">
            <i className="bi bi-check-lg" /> Approve
          </Clickable>
        )}
        {perm.allow_print && (
          <Clickable onClick={onPrint} disabled={pdfLoading} title="Print PDF (QF-PUR-03)" className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100 disabled:tw-opacity-60">
            {pdfLoading ? <i className="bi bi-arrow-repeat tw-animate-spin" /> : <i className="bi bi-file-earmark-pdf" />} PDF
          </Clickable>
        )}
        {perm.allow_print_excel && (
          <Clickable onClick={onDownloadExcel} disabled={excelLoading} title="Download Excel (fill mẫu QF-PUR-03)" className="tw-inline-flex tw-items-center tw-gap-1.5 tw-h-9 tw-px-3 tw-rounded-geist-sm tw-border tw-border-solid tw-border-geist-border tw-text-geist-fg-2 tw-text-[13px] tw-font-medium hover:tw-bg-geist-100 disabled:tw-opacity-60">
            {excelLoading ? <i className="bi bi-arrow-repeat tw-animate-spin" /> : <i className="bi bi-file-earmark-excel" />} Excel
          </Clickable>
        )}
      </div>
    </div>
  );

  return (
    <>
      <PageShell header={header}>
        <div className="tw-p-4 sm:tw-p-6 tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
          <div className="lg:tw-col-span-2 tw-space-y-4">
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-p-4">
              <SectionHeading className="tw-mb-3">Order info</SectionHeading>
              <div className="tw-grid tw-grid-cols-2 tw-gap-x-6">
                <InfoRow label="Purchase Requisition" value={po.purchase_request?.code} />
                <InfoRow label="PO date" value={po.po_date} />
                <InfoRow label="Item / Hạng mục" value={po.item_ref} />
                <InfoRow label="Ref." value={po.ref_no} />
                <InfoRow label="Cost center" value={po.cost_center} />
                <InfoRow label="Job" value={po.job} />
                <InfoRow label="Purchaser" value={po.purchaser_name} />
                <InfoRow label="Payment term" value={po.payment_term} />
                <InfoRow label="Delivery to" value={po.delivery_to} />
                <InfoRow label="Delivery time" value={po.delivery_time} />
                <InfoRow label="Enclosed Doc." value={po.enclosed_doc} />
                <InfoRow label="Certificates requested" value={po.certificates_requested} />
              </div>
            </div>

            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-p-4">
              <SectionHeading className="tw-mb-3">Supplier</SectionHeading>
              <div className="tw-grid tw-grid-cols-2 tw-gap-x-6">
                <InfoRow label="Name" value={po.supplier_name} />
                <InfoRow label="Title" value={po.supplier_title} />
                <InfoRow label="Attn." value={po.supplier_attn} />
                <InfoRow label="Phone" value={po.supplier_phone} />
                <InfoRow label="Fax" value={po.supplier_fax} />
              </div>
            </div>

            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-p-4 tw-overflow-hidden">
              <SectionHeading className="tw-mb-3">Item lines</SectionHeading>
              <table className="tw-w-full tw-border-collapse tw-text-sm">
                <thead className="tw-bg-geist-bg-2">
                  <tr className="tw-border-b tw-border-solid tw-border-geist-border">
                    <th className="tw-px-2 tw-py-2 tw-text-left tw-text-[11px] tw-text-geist-fg-3">Description</th>
                    <th className="tw-px-2 tw-py-2 tw-text-left tw-text-[11px] tw-text-geist-fg-3">Unit</th>
                    <th className="tw-px-2 tw-py-2 tw-text-right tw-text-[11px] tw-text-geist-fg-3">Qty</th>
                    <th className="tw-px-2 tw-py-2 tw-text-right tw-text-[11px] tw-text-geist-fg-3">Unit price</th>
                    <th className="tw-px-2 tw-py-2 tw-text-right tw-text-[11px] tw-text-geist-fg-3">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(po.items || []).map((i) => (
                    <tr key={i.id} className="tw-border-b tw-border-solid tw-border-geist-border last:tw-border-0">
                      <td className="tw-px-2 tw-py-2">{i.description}</td>
                      <td className="tw-px-2 tw-py-2">{i.unit}</td>
                      <td className="tw-px-2 tw-py-2 tw-text-right tw-tabular-nums">{money(i.qty)}</td>
                      <td className="tw-px-2 tw-py-2 tw-text-right tw-tabular-nums">{money(i.unit_price)}</td>
                      <td className="tw-px-2 tw-py-2 tw-text-right tw-tabular-nums">{money(i.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="tw-flex tw-justify-end tw-gap-6 tw-mt-3 tw-text-sm">
                <span className="tw-text-geist-fg-3">Subtotal: <b className="tw-text-geist-fg">{money(po.subtotal)}</b></span>
                <span className="tw-text-geist-fg-3">Tax ({po.tax_rate}%): <b className="tw-text-geist-fg">{money(po.tax_amount)}</b></span>
                <span className="tw-text-geist-fg-3">Total: <b className="tw-text-geist-fg">{money(po.total)}</b></span>
              </div>
            </div>
          </div>

          <div className="tw-space-y-4 lg:tw-sticky lg:tw-top-4 lg:tw-self-start">
            <div className="tw-bg-geist-bg tw-rounded-geist-md tw-border tw-border-solid tw-border-geist-border tw-shadow-geist-card tw-p-4">
              <SectionHeading className="tw-mb-3">Approval chain</SectionHeading>
              <div className="tw-space-y-3">
                <div className="tw-flex tw-items-start tw-gap-2.5">
                  <div className="tw-w-2 tw-h-2 tw-rounded-xl tw-bg-geist-success tw-mt-1.5 tw-flex-shrink-0" />
                  <div>
                    <div className="tw-text-[13px] tw-font-medium tw-text-geist-fg">{po.requestor?.name || "Requester"} <span className="tw-text-geist-fg-3 tw-font-normal">(Prepared by)</span></div>
                    <div className="tw-text-[11px] tw-text-geist-fg-3">{po.requestor?.email}</div>
                  </div>
                </div>
                {(po.approvals || []).map((a) => (
                  <div key={a.id} className="tw-flex tw-items-start tw-gap-2.5">
                    <div className={`tw-w-2 tw-h-2 tw-rounded-xl tw-mt-1.5 tw-flex-shrink-0 ${a.is_approved ? "tw-bg-geist-success" : a.is_rejected ? "tw-bg-geist-error" : "tw-bg-geist-fg-3"}`} />
                    <div className="tw-flex-1">
                      <div className="tw-text-[13px] tw-font-medium tw-text-geist-fg">{a.approver_name || a.approver_email} <span className="tw-text-geist-fg-3 tw-font-normal">({a.block_label})</span></div>
                      <div className="tw-text-[11px] tw-text-geist-fg-3">{a.approver_email}</div>
                      {a.signed_at && <div className="tw-text-[11px] tw-text-geist-fg-3">Signed {a.signed_at}</div>}
                      {a.comment && <div className="tw-text-[11px] tw-text-geist-fg-2 tw-italic tw-mt-0.5">"{a.comment}"</div>}
                    </div>
                  </div>
                ))}
                {(po.approvals || []).length === 0 && (
                  <div className="tw-text-[12px] tw-text-geist-fg-3 tw-italic">Not submitted yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </PageShell>

      <PurchaseOrderDrawer show={editOpen} onHide={() => setEditOpen(false)} purchaseOrder={po}
        onSaved={() => dispatch(getPurchaseOrder(po.id))} />
      <ApproveForm show={approveOpen} onHide={() => setApproveOpen(false)} purchaseOrder={po} />
      <RejectForm show={rejectOpen} onHide={() => setRejectOpen(false)} purchaseOrder={po} />
      <CancelForm show={cancelOpen} onHide={() => setCancelOpen(false)} purchaseOrder={po} />
      <Modal show={deleteOpen} onHide={() => setDeleteOpen(false)} title={`Delete ${po.code}`} size="sm"
        footer={<>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button variant="danger" size="sm" disabled={busy} onClick={onDelete}>Delete</Button>
        </>}>
        <p className="tw-text-sm tw-text-geist-fg-2 tw-m-0">Are you sure you want to delete this purchase order? This cannot be undone.</p>
      </Modal>
    </>
  );
};

export default PurchaseOrderDetail;
